import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import * as db from "@/lib/db.server";

export type LandingPageRow = {
  id: string;
  page_type: string;
  slug: string;
  specialty: string | null;
  city: string | null;
  state: string | null;
  insurance_id: string | null;
  provider_count: number;
  title: string | null;
  description: string | null;
  is_indexable: boolean;
  last_refreshed_at: string;
};

export const LANDING_PAGES_PER_DIRECTORY_PAGE = 500;
export const LANDING_PAGES_PER_SITEMAP = 10000;

/** Lightweight upsert called from landing-page route handlers as side-effect. */
export const upsertLandingPage = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        page_type: z.enum([
          "specialty_city",
          "specialty_state",
          "insurance_city",
          "specialty_insurance_city",
          "specialty_insurance",
        ]),
        slug: z.string().min(2).max(300),
        specialty: z.string().nullable().optional(),
        city: z.string().nullable().optional(),
        state: z.string().nullable().optional(),
        insurance_id: z.string().nullable().optional(),
        provider_count: z.number().int().min(0),
        title: z.string().nullable().optional(),
        description: z.string().nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    await db.execute(
      `INSERT INTO public.seo_pages
         (page_type, slug, specialty, city, state, insurance_id,
          provider_count, title, description, is_indexable, last_refreshed_at)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11)
       ON CONFLICT (slug) DO UPDATE SET
         page_type = EXCLUDED.page_type,
         specialty = EXCLUDED.specialty,
         city = EXCLUDED.city,
         state = EXCLUDED.state,
         insurance_id = EXCLUDED.insurance_id,
         provider_count = EXCLUDED.provider_count,
         title = EXCLUDED.title,
         description = EXCLUDED.description,
         is_indexable = EXCLUDED.is_indexable,
         last_refreshed_at = EXCLUDED.last_refreshed_at`,
      [
        data.page_type,
        data.slug,
        data.specialty ?? null,
        data.city ?? null,
        data.state ?? null,
        data.insurance_id ?? null,
        data.provider_count,
        data.title ?? null,
        data.description ?? null,
        data.provider_count > 0,
        new Date().toISOString(),
      ],
    );
    return { ok: true };
  });

/** Total indexable landing pages (drives /landing-pages/directory pagination). */
export const getLandingPagesTotal = createServerFn({ method: "GET" }).handler(
  async () => {
    // Original used { count: 'estimated' } but with an is_indexable filter,
    // which the reltuples estimate cannot honor; use an exact filtered count.
    const row = await db.one<{ count: number }>(
      "SELECT count(*)::int AS count FROM public.seo_pages WHERE is_indexable = true",
    );
    return { total: row?.count ?? 0 };
  },
);

/** Paginated indexable landing pages for the directory hub. */
export const getLandingPagesDirectory = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ page: z.number().int().min(0) }).parse(input))
  .handler(async ({ data }) => {
    const from = data.page * LANDING_PAGES_PER_DIRECTORY_PAGE;
    const rows = await db.query<{
      slug: string;
      title: string | null;
      provider_count: number;
      page_type: string;
    }>(
      `SELECT slug, title, provider_count, page_type
         FROM public.seo_pages
        WHERE is_indexable = true
        ORDER BY page_type ASC, slug ASC
        LIMIT $1 OFFSET $2`,
      [LANDING_PAGES_PER_DIRECTORY_PAGE, from],
    );
    return { pages: rows ?? [] };
  });

/** Slugs for a paginated XML sitemap chunk. */
export const getLandingSitemapSlugs = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ page: z.number().int().min(1) }).parse(input))
  .handler(async ({ data }) => {
    const from = (data.page - 1) * LANDING_PAGES_PER_SITEMAP;
    const rows = await db.query<{
      slug: string;
      last_refreshed_at: string;
    }>(
      `SELECT slug, last_refreshed_at
         FROM public.seo_pages
        WHERE is_indexable = true
        ORDER BY slug ASC
        LIMIT $1 OFFSET $2`,
      [LANDING_PAGES_PER_SITEMAP, from],
    );
    return { rows: rows ?? [] };
  });
